<script setup>
import CommonUtil from '@/components/CommonUtil';
import GridFileRenderer from '@/components/GridFileRenderer.vue';
import { AG_GRID_LOCALE_KR } from '@ag-grid-community/locale';
import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';

const props = defineProps({
    columnDefs: {
        type: Array,
        required: true
    },
    gridName: {
        type: String,
        required: true
    },
    rowData: {
        type: Array,
        required: true
    },
    height: {
        type: String,
        default: '100%'
    },
    width: {
        type: String,
        default: '100%'
    },
    pagination: {
        type: Boolean,
        default: false
    },
    paginationPageSize: {
        type: Number,
        default: 10
    },
    paginationPageSizeSelector: {
        type: Array,
        default: () => [10, 20, 50, 100, 200, 500]
    },
    rowSelection: {
        type: String,
        default: 'single'
    },
    preserveSelectionOnRowDataChange: {
        type: Boolean,
        default: true
    },
    showClearButton: {
        type: Boolean,
        default: false
    },
    showAddButton: {
        type: Boolean,
        default: false
    },
    showSaveButton: {
        type: Boolean,
        default: false
    },
    showDeleteButton: {
        type: Boolean,
        default: false
    },
    empty: {
        type: Boolean,
        default: false
    },
    totalList: {
        type: Object,
        default: () => []
    },
    headerHeight: {
        type: String,
        default: '35'
    },
    defaultColDef: {
        type: Object,
        default: () => ({
            sortable: false,
            filter: false,
            resizable: true,
            headerClass: 'custom-header'
        })
    },
    loading: {
        type: Boolean,
        default: false
    },
    overlayLoadingTemplate: {
        type: String,
        default: () => '로딩중'
    }
});

const currentPage = ref(1);
const selectedPageSize = ref(props.paginationPageSize);
const selectedRowIndex = ref(null);
const { proxy } = getCurrentInstance();
const gridApi = ref(null);
const totalRows = ref(0);
const totalPages = ref(1);
const agGridRef = ref(null);
const isEditing = ref(false);
const selectedRowIds = new Set();
let isUpdatingRowData = false;
let activeRowDataRef = null;
const Utils = new CommonUtil();
const loading = computed(() => props.loading);

const gridOptions = {
    headerHeight: props.headerHeight + 'px',
    pagination: true,
    paginationAutoPageSize: false,
    suppressPaginationPanel: true,
    stopEditingWhenCellsLoseFocus: true,
    // suppressRowClickSelection: true,
    // suppressCellSelection: true,
    getRowStyle: (params) => {
        return { backgroundColor: params.node.rowIndex % 2 === 0 ? '#FFFFFF' : '#F8F8F8' };
    },
    suppressRowTransform: true,
    onCellEditingStarted: () => {
        isEditing.value = true;
    },
    onCellEditingStopped: (params) => {
        isEditing.value = false;
        selectEditedRow(params);
    },
    rowSelection: props.rowSelection,
    localeText: AG_GRID_LOCALE_KR // https://www.ag-grid.com/javascript-data-grid/localisation/
    // onRowClicked: (event) => {
    //     const rowIndex = event.node.rowIndex;

    //     if (selectedRowIndex.value !== null) {
    //         const previousRow = event.api.getRowNode(selectedRowIndex.value);
    //         if (previousRow) {
    //             previousRow.setDataValue('class', null);
    //         }
    //     }

    //     selectedRowIndex.value = rowIndex;
    //     event.node.setDataValue('class', 'ag-row-selected');
    // }
};

const emit = defineEmits(['gridReady', 'selectionChanged', 'rowSelected', 'clearClicked', 'addRowClicked', 'saveClicked', 'deleteClicked', 'onRowDoubleClick', 'cellValueChanged']);

// 현재 페이지에 표시할 데이터
const prevPage = () => {
    if (gridApi.value && currentPage.value > 1) {
        const newPage = currentPage.value - 2;
        const startRow = newPage * selectedPageSize.value;
        gridApi.value.ensureIndexVisible(startRow);
        updatePaginationState();
    }
};

const nextPage = () => {
    if (gridApi.value && currentPage.value < totalPages.value) {
        const startRow = currentPage.value * selectedPageSize.value;
        gridApi.value.ensureIndexVisible(startRow);
        updatePaginationState();
    }
};

const changePageSize = () => {
    if (gridApi.value) {
        gridApi.value.setGridOption('paginationPageSize', Number(selectedPageSize.value));
        gridApi.value.ensureIndexVisible(0);
        updatePaginationState();
    }
};

const updatePaginationState = () => {
    if (gridApi.value) {
        try {
            const rowCount = gridApi.value.getDisplayedRowCount();
            totalRows.value = rowCount;
            const pageSize = selectedPageSize.value;
            totalPages.value = Math.ceil(rowCount / pageSize);
            currentPage.value = Math.floor(gridApi.value.getFirstDisplayedRowIndex() / pageSize) + 1;
        } catch (error) {
            console.error('Error updating pagination state:', error);
        }
    }
};
const goToPage = (page) => {
    if (gridApi.value && page > 0 && page <= totalPages.value) {
        const startRow = (page - 1) * selectedPageSize.value;
        gridApi.value.ensureIndexVisible(startRow);
        updatePaginationState();
    }
};

const updateRowData = (newData) => {
    if (gridApi.value) {
        activeRowDataRef = newData;
        isUpdatingRowData = true;

        if (!props.preserveSelectionOnRowDataChange) {
            selectedRowIds.clear();
            gridApi.value.deselectAll();
        }

        gridApi.value.setGridOption('rowData', newData);
        nextTick(() => {
            if (props.preserveSelectionOnRowDataChange) {
                restoreSelectedRows();
            }
            updatePaginationState();
            setTimeout(() => {
                if (props.preserveSelectionOnRowDataChange) {
                    restoreSelectedRows();
                }
                isUpdatingRowData = false;
            }, 0);
        });
    }
};

const onPaginationChanged = () => {
    updatePaginationState();
};

const onGridReady = (params) => {
    if (params.api) {
        gridApi.value = params.api;
        gridApi.value.setGridOption('paginationPageSize', Number(selectedPageSize.value));
        updateRowData(props.rowData);

        nextTick(() => {
            const removeAriaHidden = () => {
                const elements = agGridRef.value.$el.querySelectorAll('[aria-hidden="true"]');
                elements.forEach((el) => {
                    el.removeAttribute('aria-hidden');
                });
            };

            // 페이지 내용이 완전히 로드될 때까지 기다리기
            setTimeout(removeAriaHidden, 0); // 0ms 지연 후 실행
        });
    }
    emit('gridReady', params);
};

const copyToClipboard = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
        }
    } catch (err) {}
};

const onCellClicked = (event) => {
    const { column, value, data, node } = event;
    const colDef = column.getColDef();

    if (colDef.editable === false || (colDef.editable === undefined && !colDef.cellEditor)) {
        let displayText = '';

        if (colDef.valueFormatter) {
            displayText = colDef.valueFormatter({
                value: value,
                data: data,
                node: node,
                colDef: colDef
            });
        } else if (colDef.cellRenderer) {
            try {
                const cellRendererInstances = gridApi.value.getCellRendererInstances({
                    rowNodes: [node],
                    columns: [column.getColId()]
                });

                if (cellRendererInstances && cellRendererInstances.length > 0) {
                    const cellElement = cellRendererInstances[0].getGui();
                    displayText = cellElement.innerText || cellElement.textContent || '';
                } else {
                    displayText = String(value || '');
                }
            } catch (error) {
                displayText = String(value || '');
            }
        } else {
            displayText = String(value || '');
        }

        if (displayText.trim() !== '') {
            copyToClipboard(displayText);
        }
    }
};

function refreshSelectionCheckbox(api, node) {
    if (!api || !node || !api.getColumnDef('boolean')) {
        return;
    }

    api.refreshCells({
        rowNodes: [node],
        columns: ['boolean'],
        force: true
    });
    api.redrawRows({ rowNodes: [node] });
}

function rememberSelectedRow(rowId, value) {
    if (rowId === null || rowId === undefined) {
        return;
    }

    const normalizedRowId = String(rowId);

    if (value && props.rowSelection === 'single') {
        selectedRowIds.clear();
    }

    if (value) {
        selectedRowIds.add(normalizedRowId);
    } else {
        selectedRowIds.delete(normalizedRowId);
    }
}

function restoreSelectedRows() {
    if (!gridApi.value || selectedRowIds.size === 0) {
        return;
    }

    const rowNodes = [];
    gridApi.value.forEachNode((node) => {
        if (selectedRowIds.has(String(node.id))) {
            node.setSelected(true);
            rowNodes.push(node);
        }
    });

    if (rowNodes.length > 0 && gridApi.value.getColumnDef('boolean')) {
        gridApi.value.refreshCells({
            rowNodes,
            columns: ['boolean'],
            force: true
        });
        gridApi.value.redrawRows({ rowNodes });
    }
}

function setRowBooleanValue(rowId, value) {
    if (!gridApi.value) {
        return;
    }

    rememberSelectedRow(rowId, value);

    let targetNode = null;
    gridApi.value.forEachNode((node) => {
        if (String(node.id) === String(rowId)) {
            targetNode = node;
        }
    });

    if (!targetNode) {
        return;
    }

    const updateSelection = () => {
        targetNode.setSelected(!!value);
        refreshSelectionCheckbox(gridApi.value, targetNode);
    };

    updateSelection();
    nextTick(updateSelection);
    setTimeout(updateSelection, 0);
}

function selectEditedRow(params) {
    if (!params?.node || params.column?.getColId?.() === 'boolean' || params.valueChanged === false) {
        return;
    }

    setRowBooleanValue(params.node.id, true);
}

const onCellValueChanged = (params) => {
    const { column, data, node } = params;

    if (column.getColId() !== 'boolean') {
        setRowBooleanValue(node.id, true);
    }

    setCellTextColor([
        {
            rowId: node.id,
            field: column.getColId(),
            color: null
        }
    ]);

    emit('cellValueChanged', {
        gridName: props.gridName,
        rowData: data,
        rowId: node.id,
        column: column.getColId(),
        newValue: params.newValue,
        oldValue: params.oldValue
    });
};

const onSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedNodes = event.api.getSelectedNodes();

    if (isUpdatingRowData && selectedNodes.length === 0) {
        return;
    }

    selectedRowIds.clear();
    selectedNodes.forEach((node) => {
        selectedRowIds.add(String(node.id));
    });

    // if (selectedRows.length > 0) {
    const emitData = selectedRows.map((selectedRow, index) => ({
        row: selectedRow,
        //rowIndex: selectedNodes[index].rowIndex,
        rowId: selectedNodes[index].id // rowNode의 id 사용
    }));

    emit('selectionChanged', {
        gridName: props.gridName,
        selectedRows: emitData
    });
    // }
};
const onCellDoubleClicked = (e) => {
    const isNewRow = e.data && e.data.isNew ? e.data.isNew : false;
    let selectedRows = { row: e.data, rowID: e.node.id, newRow: isNewRow };

    emit('onRowDoubleClick', {
        gridName: props.gridName,
        selectedRows: [selectedRows]
    });
};

let alreadySelected = false;
const onRowSelected = (event) => {
    if (alreadySelected) {
        return;
    }
    alreadySelected = true;
    //console.log('Row selected', event);
    setTimeout(() => {
        alreadySelected = false;
        if (event.api.getColumnDefs().some((item) => item.field === 'use_state' && item.cellEditor === 'agCheckboxCellEditor') && event.source === 'rowClicked') {
            event.node.setSelected(true);
        }

        // 상태가 반영된 후, `rowSelected` 이벤트 발행
        const selectedRow = event.node.data;
        emit('rowSelected', selectedRow);
    }, 100); // 잠시 후 상태를 초기화하여 중복 호출을 방지
};

const onModelUpdated = () => {
    nextTick(() => {
        updatePaginationState();
    });
};

const scrollToRow = (index, position = 'bottom') => {
    if (gridApi.value) {
        gridApi.value.ensureIndexVisible(index, position);
    }
};

/* const columnDefs = props.columnDefs.map((col, index) => ({
    ...col,
    cellStyle: (params) => {
        if (params.data && Object.hasOwn(params.data, 'isNew') && params.data.isNew) {
            return { backgroundColor: '#e8f0fe', color: '#1c1c1c' };
        }
         return null;
    }
})); */
//추가된 행의 배경색을 표시하기위함
const columnDefs = computed(() => {
    //기존처리
    // return props.columnDefs.map((col, index) => ({
    //     ...col,
    //     cellStyle: (params) => {
    //         if (params.data && Object.hasOwn(params.data, 'isNew') && params.data.isNew) {
    //             return { backgroundColor: '#e8f0fe', color: '#1c1c1c' };
    //         }
    //         return null;
    //     }
    // }));

    return props.columnDefs.map((col) => {
        // 체크박스 컬럼은 따로 처리
        const isCheckbox = col.cellEditor === 'agCheckboxCellEditor';

        return {
            ...col,
            minWidth: isCheckbox ? 65 : col.minWidth,
            maxWidth: isCheckbox ? 65 : col.maxWidth,
            cellStyle: (params) => {
                const baseStyle = {};

                if (params.data?.isNew) {
                    Object.assign(baseStyle, {
                        backgroundColor: '#e8f0fe',
                        color: '#1c1c1c'
                    });
                }

                if (!isCheckbox) {
                    Object.assign(baseStyle, {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    });
                }

                // 컬럼의 기존 cellStyle 적용
                if (typeof col.cellStyle === 'function') {
                    Object.assign(baseStyle, col.cellStyle(params));
                } else if (typeof col.cellStyle === 'object') {
                    Object.assign(baseStyle, col.cellStyle);
                }

                return baseStyle;
            },
            tooltipValueGetter: !isCheckbox
                ? (params) => {
                      return params.valueFormatted ?? params.value ?? '';
                  }
                : undefined
        };
    });
});

const onAddRowClicked = (newRowData) => {
    if (gridApi.value) {
        // single 행 일 경우 하나의 행만 추가되도록 수정
        if (props.rowSelection === 'single') {
            const rowCount = gridApi.value.getDisplayedRowCount();
            const hasNewRow = rowCount > 0 && gridApi.value.getDisplayedRowAtIndex(rowCount - 1).data.isNew;
            if (hasNewRow) {
                console.warn('단일 선택 모드에서는 하나의 행만 추가할 수 있습니다.');
                return;
            }
        }

        const addRowData = {
            ...newRowData,
            isNew: true
        };

        // 현재 선택된 행들의 ID 저장
        const selectedNodes = gridApi.value.getSelectedNodes();
        const selectedRowIds = selectedNodes.map((node) => node.id);

        // 새 행 추가
        gridApi.value.applyTransaction({ add: [addRowData] });

        // 페이지네이션 상태 업데이트
        nextTick(() => {
            updatePaginationState();

            // 모든 선택 해제
            gridApi.value.deselectAll();

            // 이전에 선택된 행들 다시 선택
            selectedRowIds.forEach((id) => {
                const rowNode = gridApi.value.getRowNode(id);
                if (rowNode) {
                    rowNode.setSelected(true);
                }
            });

            // 새로 추가된 행 선택
            const lastIndex = gridApi.value.getDisplayedRowCount() - 1;
            const newRowNode = gridApi.value.getDisplayedRowAtIndex(lastIndex);

            // 새 행이 보이도록 스크롤
            setTimeout(() => {
                gridApi.value.ensureIndexVisible(lastIndex, 'bottom');
                if (newRowNode) {
                    newRowNode.setSelected(true);

                    //'boolean' 열이 아닌 다른 열이 있는지 확인
                    const hasBooleanColumn = gridApi.value.getColumnDefs().some((col) => col.field === 'boolean');
                    if (!hasBooleanColumn) {
                        setRowBooleanValue(newRowNode.id, true);
                        gridApi.value.refreshCells({
                            rowNodes: [newRowNode],
                            columns: ['boolean'],
                            force: true
                        });
                    }

                    // cellValueChanged 이벤트 발생
                    // const booleanColumn = gridApi.value.getColumnDef('boolean');
                    // if (booleanColumn) {
                    //     emit('cellValueChanged', {
                    //         gridName: props.gridName,
                    //         rowData: newRowNode.data,
                    //         column: 'boolean',
                    //         newValue: true,
                    //         oldValue: undefined
                    //     });
                    // }
                }
            }, 100);
        });

        emit('addRowClicked', addRowData);
    }
};

const onClearClicked = () => {
    emit('clearClicked');
};

const onSaveClicked = () => {
    emit('saveClicked');
};

const onDeleteClicked = () => {
    emit('deleteClicked');
};

const onRowClicked = (e) => {
    if (!e || !e.node || !e.api) return; //  || isEditing.value

    const rowIndex = e.node.rowIndex;
    const isNewRow = e.data && e.data.isNew ? e.data.isNew : false;
    let selectedRows = { row: e.data, rowID: e.node.id, newRow: isNewRow };

    emit('selectionChanged', {
        gridName: props.gridName,
        selectedRows: [selectedRows]
    });
};

const getAllGridData = () => {
    if (gridApi.value) {
        const allData = [];
        gridApi.value.forEachNode((node) => {
            allData.push(node.data);
        });
        return allData;
    }
    return [];
};

// 전체 체크     ex) gridRef.value.checkAll();
const checkAll = () => {
    if (gridApi.value) {
        gridApi.value.forEachNode((node) => {
            node.setSelected(true);
        });
    }
};

// 전체 체크해제     ex) gridRef.value.uncheckAll();
const uncheckAll = () => {
    if (gridApi.value) {
        gridApi.value.forEachNode((node) => {
            node.setSelected(false);
        });
    }
};

// 특정 필드명의 값이 동일한 경우 체크     ex) gridRef.value.checkByValue('vehicle_group_id', 'group1');
const checkByValue = (fieldName, targetValue) => {
    if (gridApi.value) {
        gridApi.value.forEachNode((node) => {
            if (node.data[fieldName] === targetValue) {
                node.setSelected(true);
            }
        });
    }
};

// 특정 필드명의 값이 동일한 경우 멀티 체크     ex) gridRef.value.checkByValue('vehicle_group_id', , ['group1', 'group2']);
const checkByMultipleValues = (fieldName, targetValues) => {
    if (gridApi.value && Array.isArray(targetValues)) {
        gridApi.value.forEachNode((node) => {
            if (targetValues.includes(node.data[fieldName])) {
                node.setSelected(true);
            }
        });
    }
};

// 특정 그룹의 체크 상태를 변경
const toggleRowsByField = (fieldName, targetValue, checked) => {
    if (gridApi.value) {
        gridApi.value.forEachNode((node) => {
            if (node.data[fieldName] === targetValue) {
                node.setSelected(checked);
            }
        });
    }
};

// 선택된 행들의 데이터를 반환     ex) gridRef.value.getSelectedRows()
const getSelectedRows = () => {
    if (gridApi.value) {
        return gridApi.value.getSelectedRows();
    }
    return [];
};

const getExcelValue = (colDef, value, rowNode) => {
    if (typeof colDef.excelValueGetter === 'function') {
        return colDef.excelValueGetter({
            value,
            data: rowNode.data,
            node: rowNode,
            colDef
        });
    }

    if (colDef.valueFormatter) {
        return colDef.valueFormatter({
            value,
            data: rowNode.data
        });
    }

    return value;
};

const exportToExcel = async (val, excludedFields = []) => {
    if (!gridApi.value) return;

    // Create a processing function to get displayed values
    const processDataFromGrid = (params) => {
        const processedData = [];

        // Process the rows
        params.data.forEach((rowNode) => {
            const rowData = {};
            // Only process visible columns excluding checkbox and hidden columns
            params.columnKeys
                .filter((key) => {
                    const colDef = gridApi.value.getColumnDef(key);
                    return colDef && colDef.field !== 'checkBox' && !colDef.checkboxSelection && !colDef.hide && !excludedFields.includes(colDef.field) && colDef.headerName;
                })
                .forEach((key) => {
                    const colDef = gridApi.value.getColumnDef(key);
                    const value = gridApi.value.getValue(key, rowNode);

                    if (typeof colDef.excelValueGetter === 'function') {
                        rowData[key] = getExcelValue(colDef, value, rowNode);
                        return;
                    }

                    // Get the cell
                    const cellComp = gridApi.value.getCellRendererInstances({
                        rowNodes: [rowNode],
                        columns: [key]
                    })[0];

                    if (cellComp) {
                        // Get displayed text from renderer
                        rowData[key] = cellComp.getGui().innerText;
                    } else {
                        // Get formatted value or raw value
                        rowData[key] = getExcelValue(colDef, value, rowNode);
                    }
                });

            processedData.push(rowData);
        });

        return processedData;
    };

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Get column definitions (excluding checkBox and hidden columns)
    const columnDefs = gridApi.value.getColumnDefs().filter((col) => col.field !== 'checkBox' && !col.checkboxSelection && !col.hide && !excludedFields.includes(col.field) && col.headerName);

    // Add headers
    const headers = columnDefs.map((col) => col.headerName);
    const headerRow = worksheet.addRow(headers);

    // Format headers
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FF0038EA' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };
    });

    // Get displayed data
    const rowData = [];
    gridApi.value.forEachNodeAfterFilter((node) => rowData.push(node));

    const processedData = processDataFromGrid({
        data: rowData,
        columnKeys: columnDefs.map((col) => col.field)
    });

    // Add data rows
    processedData.forEach((rowData) => {
        const values = columnDefs.map((col) => rowData[col.field]);
        const row = worksheet.addRow(values);

        // Apply cell styling
        columnDefs.forEach((col, index) => {
            if (col.cellClass) {
                let alignment = { vertical: 'middle' };
                alignment.horizontal = 'left';

                if (col.cellClass.includes('alignC')) {
                    alignment.horizontal = 'center';
                } else if (col.cellClass.includes('alignL')) {
                    alignment.horizontal = 'left';
                } else if (col.cellClass.includes('alignR')) {
                    alignment.horizontal = 'right';
                }

                row.getCell(index + 1).alignment = alignment;
            }
        });
    });

    // Auto-adjust column width and set borders
    worksheet.columns.forEach((column, index) => {
        column.width = 15;
        worksheet.getColumn(index + 1).eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
    });

    // Add filter
    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: headers.length }
    };

    // Create and download Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), val + '.xlsx');
};

const createExcelWorkbook = async () => {
    if (!gridApi.value) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    const filteredColumns = gridApi.value.getColumnDefs().filter((col) => col.field !== 'checkBox' && !col.checkboxSelection && !col.hide && col.headerName);

    // Add headers
    const headerRow = worksheet.addRow(filteredColumns.map((col) => col.headerName));
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FF0038EA' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };
    });

    // Get the displayed values instead of raw data
    const displayedData = [];
    gridApi.value.forEachNode((node) => {
        const rowData = {};
        filteredColumns.forEach((col) => {
            // Get the displayed value instead of the raw data
            const cellValue = gridApi.value.getValue(col.field, node);

            // For custom components (like select boxes), get their displayed text
            if (typeof col.excelValueGetter === 'function') {
                rowData[col.field] = getExcelValue(col, cellValue, node);
            } else if (col.cellRenderer) {
                // If using a custom renderer, get the displayed text
                const cellComp = gridApi.value.getCellRendererInstances({
                    rowNodes: [node],
                    columns: [col.field]
                })[0];

                if (cellComp) {
                    // Try to get the displayed text from the component
                    rowData[col.field] = cellComp.getGui().innerText;
                } else {
                    // Fall back to formatted value if available
                    rowData[col.field] = col.valueFormatter ? col.valueFormatter({ value: cellValue, data: node.data }) : cellValue;
                }
            } else if (col.valueFormatter) {
                // If there's a value formatter, use it
                rowData[col.field] = getExcelValue(col, cellValue, node);
            } else {
                // Otherwise use the raw value
                rowData[col.field] = getExcelValue(col, cellValue, node);
            }
        });
        displayedData.push(rowData);
    });

    // Add data
    displayedData.forEach((rowData) => {
        const rowValues = filteredColumns.map((col) => rowData[col.field]);
        const row = worksheet.addRow(rowValues);

        // Apply cell alignment
        filteredColumns.forEach((col, index) => {
            if (col.cellClass) {
                let alignment = { vertical: 'middle' };
                alignment.horizontal = 'left';

                if (col.cellClass.includes('alignC')) {
                    alignment.horizontal = 'center';
                } else if (col.cellClass.includes('alignL')) {
                    alignment.horizontal = 'left';
                } else if (col.cellClass.includes('alignR')) {
                    alignment.horizontal = 'right';
                }
                row.getCell(index + 1).alignment = alignment;
            }
        });
    });

    // Auto-adjust column width and set borders
    worksheet.columns.forEach((column, index) => {
        column.width = 15;
        worksheet.getColumn(index + 1).eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
    });

    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: filteredColumns.length }
    };

    return workbook;
};

const exportToExcelData = async (data, fileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // 헤더 추가
    const headers = Object.keys(data[0]);
    worksheet.addRow(headers);

    // 스타일 적용
    worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FF0038EA' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };
    });

    // 데이터 추가
    data.forEach((item) => {
        const row = worksheet.addRow(Object.values(item));
        row.eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });
    });

    // 열 너비 자동 조정 및 테두리 설정
    worksheet.columns.forEach((column, index) => {
        column.width = 15;
        worksheet.getColumn(index + 1).eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
    });

    // 필터 추가
    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: headers.length }
    };

    // 엑셀 파일 생성 및 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), `${fileName}.xlsx`);
};

const setCellTextColor = (colorList) => {
    if (gridApi.value) {
        colorList.forEach(({ rowId, field, color }) => {
            const rowNode = gridApi.value.getRowNode(rowId);
            if (rowNode) {
                const colDef = gridApi.value.getColumnDef(field);
                if (colDef) {
                    colDef.cellStyle = (params) => {
                        if (params.data.id === rowId && params.colDef.field === field) {
                            return color ? { color } : null;
                        }
                        return null;
                    };
                }
            }
        });

        // gridApi.value.refreshCells({
        //     force: true,
        //     rowNodes: colorList.map((item) => gridApi.value.getRowNode(item.rowId)),
        //     columns: colorList.map((item) => item.field)
        // });
    }
};

watch(
    () => props.rowData,
    (newData) => {
        if (newData === activeRowDataRef) {
            return;
        }

        updateRowData(newData);
    },
    { deep: true }
);

const showClearButton = ref(props.showClearButton);
const showSaveButton = ref(props.showSaveButton);
const showDeleteButton = ref(props.showDeleteButton);

watch(
    () => props.showClearButton,
    (newVal) => {
        showClearButton.value = newVal;
    }
);

watch(
    () => props.showAddButton,
    (newVal) => {
        if (newVal) {
            onAddRowClicked({}); // 초기 빈 데이터로 호출. 부모로부터 데이터 전달 시 사용
        }
    },
    { immediate: true }
);

watch(
    () => props.showSaveButton,
    (newVal) => {
        showSaveButton.value = newVal;
    }
);

watch(
    () => props.showDeleteButton,
    (newVal) => {
        showDeleteButton.value = newVal;
    }
);

// 그리드에 컴포넌트 직접등록
const frameworkComponents = {
    GridFileRenderer: GridFileRenderer
    //customSelectEditor: Utils.createCustomSelectEditor()
};

defineExpose({
    onAddRowClicked,
    exportToExcel,
    exportToExcelData,
    createExcelWorkbook,
    getAllGridData,
    checkAll,
    uncheckAll,
    checkByValue,
    checkByMultipleValues,
    toggleRowsByField,
    setRowBooleanValue,
    getSelectedRows,
    setCellTextColor,
    GridFileRenderer
});
</script>

<template>
    <div class="main-grid">
        <div class="container flex justify-end">
            <div v-if="props.totalList && (Array.isArray(props.totalList) ? props.totalList.length > 0 : Object.keys(props.totalList).length > 0)" class="flex justify-start">
                <!-- <div class="container">
            <div v-if="props.totalList && (Array.isArray(props.totalList) ? props.totalList.length > 0 : Object.keys(props.totalList).length > 0)" class="label-container"> -->
                <template v-for="(item, index) in Array.isArray(props.totalList) ? props.totalList : [props.totalList]" :key="index">
                    <div class="label-container">
                        <span v-for="(value, key) in item" :key="key" class="label"> {{ key }} : {{ value }} </span>
                    </div>
                </template>
            </div>
            <div class="buttons-container" :class="{ 'empty-container': props.empty }">
                <button v-show="props.showClearButton" @click="onClearClicked">초기화</button>
                <button v-show="props.showAddButton" @click="onAddRowClicked">행 추가</button>
                <button v-show="props.showSaveButton" @click="onSaveClicked">저장</button>

                <button v-show="props.showDeleteButton" @click="onDeleteClicked">삭제</button>
            </div>
        </div>

        <div class="ag-theme-alpine h-full w-full">
            <!--  :columnDefs="pros.columnDefs"  -->
            <ag-grid-vue
                ref="agGridRef"
                :columnDefs="columnDefs"
                :gridName="props.gridName"
                :style="{ height: height, width: width }"
                :rowData="props.rowData"
                :gridOptions="gridOptions"
                :defaultColDef="props.defaultColDef"
                :pagination="true"
                :paginationPageSize="selectedPageSize"
                :paginationPageSizeSelector="props.paginationPageSizeSelector"
                :rowSelection="props.rowSelection"
                :suppressRowClickSelection="true"
                :frameworkComponents="frameworkComponents"
                :loading="loading"
                :overlayLoadingTemplate="props.overlayLoadingTemplate"
                @grid-ready="onGridReady"
                @selection-changed="onSelectionChanged"
                @row-selected="onRowSelected"
                @rowClicked="onRowClicked"
                @cell-value-changed="onCellValueChanged"
                @cellDoubleClicked="onCellDoubleClicked"
                @cellClicked="onCellClicked"
            />
        </div>
        <div class="pagination-container">
            <div class="pagination-controls">
                <button @click="prevPage" :disabled="currentPage === 1">&lt;</button>
                <span>Page {{ currentPage }} of {{ totalPages === 0 ? 1 : totalPages }}</span>
                <button @click="nextPage" :disabled="currentPage === totalPages">></button>
                <div class="page-size">
                    <select v-model="selectedPageSize" @change="changePageSize">
                        <option v-for="size in props.paginationPageSizeSelector" :key="size" :value="size">{{ size }}</option>
                    </select>
                </div>
            </div>
            <div class="total-rows" style="margin-right: 0.2rem">
                <span>Total: {{ totalRows }}</span>
            </div>
        </div>
    </div>
</template>

<style>
.ag-theme-alpine .ag-row-selected {
    background-color: rgba(0, 56, 234, 0.01) !important;
    color: black !important;
}

.ag-theme-alpine .row-clicked {
    background-color: #ffeeba !important; /* 클릭된 행을 연한 노란색으로 강조 */
}

/* 선택된 행에 호버했을 때의 스타일 */
.ag-theme-alpine .ag-row-selected.ag-row-hover {
    background-color: rgba(0, 56, 234, 0.2) !important;
    color: black !important;
}

/* 호버했을 때의 스타일 */
.ag-theme-alpine .ag-row-hover {
    background-color: rgba(0, 56, 234, 0.2) !important;
    color: black !important;
}

.ag-theme-alpine .ag-input-field-input {
    background-color: rgba(250, 217, 155, 0.5) !important;
    border: none;
    outline: none;
    color: black;
}
.ag-theme-alpine .ag-cell-inline-editing {
    background-color: rgba(250, 217, 155, 0.5) !important;
    color: black;
}

.ag-theme-alpine .ag-input-field-input::placeholder {
    color: rgb(155, 101, 1);
}

.ag-theme-alpine .ag-checkbox-input-wrapper {
    background-color: white;
}

/* .ag-cell-value.ag-cell.ag-cell-not-inline-editing.ag-cell-normal-height {
    z-index: 0;
} */

.ag-theme-alpine .ag-paging-panel {
    display: none !important;
}
/* .ag-theme-alpine .ag-row-hover {
    background-color: #e5ecf9 !important;
} */

.ag-theme-alpine .ag-cell {
    padding: 6px 2px !important;
}

.ag-theme-alpine .ag-cell.ag-cell-value.ag-cell-not-inline-editing.ag-cell-normal-height.ag-column-first.alignL {
    padding-left: 1rem !important;
}

.ag-theme-alpine .ag-cell.ag-cell-value.ag-cell-not-inline-editing.ag-cell-normal-height.alignC {
    display: flex;
    justify-content: center;
    align-items: center;
}

.ag-theme-alpine .ag-cell.ag-cell-not-inline-editing.ag-cell-normal-height.ag-column-first.alignC {
    display: flex;
    justify-content: center;
    align-items: center;
}

.ag-theme-alpine .ag-cell.ag-cell-value.ag-cell-not-inline-editing.ag-cell-normal-height.alignR {
    padding-right: 1rem !important;
}

.pagination-container {
    display: flex;
    justify-content: space-between; /* 변경: center에서 space-between으로 */
    align-items: center;
    width: 100%;
    margin: 0.3rem 0;
    padding: 0.2rem 0;
    border-top: 0.1rem solid var(--surface-border);
    border-bottom: 0.1rem solid var(--surface-border);
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 auto; /* 중앙 정렬을 위해 추가 */
}

.left-button {
    position: absolute;
    left: 0;
    margin-left: 10px;
}

.left-button button {
    padding: 5px 10px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.left-button button:hover {
    background-color: #0056b3;
}

.total-controls {
    display: flex;
    align-items: center;
    gap: 10px;
}

.page-info {
    display: flex;
    align-items: center;
}

.page-size {
    display: flex;
    align-items: center;
}

.page-size select {
    width: 4rem;
    padding: 0.2rem;
    border: 0.1rem solid #ccc;
    border-radius: 0.6rem;
}

.pagination-controls button {
    padding: 5px 10px;
    background-color: #fff;
    cursor: pointer;
    /* border: 1px solid #ccc;
    border-radius: 4px; */
}

.pagination-controls button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.buttons-container {
    display: flex;
    gap: 4px;
    margin-bottom: 0.3rem;
    margin-top: 0.5rem;
}

.empty-container {
    height: 2rem;
}

.buttons-container button {
    /* padding: 5px 10px; */
    height: 2rem;
    background-color: white;
    color: black;
    border: solid;
    border-radius: 0.375rem;
    border-color: #e5e7eb;
    border-width: 0.1rem;
    cursor: pointer;
    width: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
}

.buttons-container button:hover {
    border-color: #0056b3;
}

.container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
}
.main-grid {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
}

.label-container {
    border-radius: 3rem;
    /* border-color: #e5e7eb; */
    /* border-width: 0.1rem; */
    /* border-left: none; */
    /* border-right: none; */
    background-color: #f8f8f8;
    padding: 0.5rem 1.3rem 0.5rem 1.3rem;
    display: flex;
    height: 1.5rem;
    /* height: 1.5rem; */
    align-items: center;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    gap: 2rem;
    margin-right: auto;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    opacity: 0.8;
    /* margin: 0.5rem 0rem 0.5rem auto; */
}

/*
.add-row-button button {
    padding: 5px 10px;
    background-color: white;
    color: black;
    border-radius: 4px;
    cursor: pointer;
}

.add-row-button :hover {
    border: 1px solid var(--linkbrary-primary-color, #0038ea);
} */
</style>

<style scoped>
:deep(.custom-header) {
    background-color: #fafafa;
    color: black;
    border-bottom: 0.3px solid #f5f5f5; /* #ccc;*/
}

:deep(.ag-header-cell-comp-wrapper) {
    display: flex;
    justify-content: center;
    align-items: center;
    /* font-weight: bolder; */
    font-size: 0.9rem;
    font-family: 'SCoreDream400';
}
:deep(.ag-header-cell-label) {
    display: flex;
    justify-content: center;
    align-items: center;
    /* font-weight: bolder; */
    font-size: 0.92rem;
    font-family: 'SCoreDream400';
}
:deep(.ag-cell) {
    font-size: 0.91rem;
    font-family: 'SCoreDream400';
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar) {
    /*width: 1rem;  스크롤바 너비 */
    height: 0.5rem; /* 스크롤바 높이 */
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track) {
    background: #f1f1f1; /* 스크롤바 배경 색상 */
    border-radius: 8px; /* 스크롤바 둥글기 */
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb) {
    background: #c9c6c6; /* 스크롤바 색상 */
    border-radius: 8px; /* 스크롤바 둥글기 */
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb:hover) {
    background: #727272; /* 스크롤바 호버 색상 */
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar) {
    width: 0.5rem; /* 스크롤바 너비 */
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-track) {
    background: #f1f1f1; /* 스크롤바 배경 색상 */
    border-radius: 8px; /* 스크롤바 둥글기 */
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb) {
    background: #c9c6c6; /* 스크롤바 색상 */
    border-radius: 8px; /* 스크롤바 둥글기 */
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb:hover) {
    background: #727272; /* 스크롤바 호버 색상 */
}
:deep(.ag-ltr .ag-header-viewport .ag-header-cell-resize::after) {
    visibility: hidden;
}

:deep(.ag-header-cell-text) {
    white-space: pre-line;
    text-align: center;
}

:deep(.ag-cell) {
    display: flex;
    align-items: center;
    padding: 6px 8px !important;
}

:deep(.ag-cell-wrapper) {
    width: 100%;
    padding: 0;
}
</style>
